import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import * as Sentry from '@sentry/nextjs';
import { IconX } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import {
  MarketplaceLocationSuggestionsDocument,
  type MarketplacePropertySuggestionFragment,
  type MarketplaceZoneType,
} from 'components/marketplace/map/__generated__/location-search.zeus';
import { formatCurrency } from 'helpers/formatters';
import { useDebounce } from 'use-debounce';

import {
  addressFromRecentEntry,
  mapLocationFromRecentEntry,
  recentEntryDisplayName,
  recentEntryFromAddress,
  recentEntryFromLocation,
  recentEntryIdentity,
  type RecentLocationSearchEntry,
} from './recentLocationSearches';
import type { MapLocation } from './types';
import { useRecentLocationSearches } from './useRecentLocationSearches';

const zoneTypeLabel = {
  CITY: '(City)',
  COUNTY: '(County)',
  MACRO_NEIGHBORHOOD: '(Neighborhood)',
  NEIGHBORHOOD: '(Neighborhood)',
  RESIDENTIAL_BOUNDARY: '',
  STATE: '',
  SUBNEIGHBORHOOD: '(Neighborhood)',
  UNKNOWN: '',
  ZIP_CODE: '(Zip code)',
} satisfies Record<MarketplaceZoneType, string>;

export interface LocationSearchProps {
  isFocused: boolean;
  selectedLocation?: MapLocation;
  setSelectedLocation: (location?: MapLocation) => void;
  selectedAddress?: MarketplacePropertySuggestionFragment;
  setSelectedAddress: (address?: MarketplacePropertySuggestionFragment) => void;
  /** Input height. `sm` (56px) on the desktop quick-filters bar; `xs` (36px) in
   * the compact mobile header. */
  size?: 'xs' | 'sm' | 'base' | 'lg';
}

const QUERY_STRING_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

type RankedMapLocation = MapLocation & { population?: number };

const reportLocationSuggestionsError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { feature: 'marketplace-browse', operation: 'locationSuggestions' },
  });
};

const fetchLocationSuggestions = (query: string, signal: AbortSignal) => {
  const types: MarketplaceZoneType[] = /^\d/u.test(query)
    ? ['ZIP_CODE']
    : ['CITY', 'NEIGHBORHOOD', 'SUBNEIGHBORHOOD'];
  return requestZeusFromBrowser(
    MarketplaceLocationSuggestionsDocument,
    { propertyInput: { query }, zoneInput: { query, types } },
    signal,
  );
};

export const MapLocationSearch = ({
  isFocused,
  selectedAddress,
  setSelectedAddress,
  selectedLocation,
  setSelectedLocation,
  size = 'sm',
}: LocationSearchProps) => {
  const [query, setQuery] = useState('');
  const [selectionLabel, setSelectionLabel] = useState<string>();
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const resultsRef = useRef<HTMLElement | null>(null);
  const { recentSearches, rememberRecentSearch, removeRecentSearch, clearRecentSearches } =
    useRecentLocationSearches();
  const suggestions = useQuery({
    queryKey: ['marketplace', 'location-suggestions', debouncedQuery],
    queryFn: ({ signal }) => fetchLocationSuggestions(debouncedQuery, signal),
    enabled: debouncedQuery.length > QUERY_STRING_MIN_LENGTH,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
  const isCurrentQuery = debouncedQuery === query;
  const isFetchingSuggestions =
    query.length > QUERY_STRING_MIN_LENGTH && (!isCurrentQuery || suggestions.isFetching);
  const fetchError = isCurrentQuery && suggestions.isError;
  const data = query.length > QUERY_STRING_MIN_LENGTH ? suggestions.data : undefined;
  const locationOptions = useMemo(() => {
    const rankedLocations: RankedMapLocation[] = (data?.marketplace.searchZones ?? []).map(
      ({ zone }) => ({
        coordinates: zone.center
          ? ([zone.center.longitude, zone.center.latitude] satisfies [number, number])
          : undefined,
        label: [zone.name, zone.stateAbbr].filter(Boolean).join(', '),
        population: zone.population ?? undefined,
        value: zone.id,
        zoneType: zone.type,
      }),
    );
    rankedLocations.sort((a, b) =>
      a.population == null || b.population == null ? 0 : b.population - a.population,
    );
    return rankedLocations.map(({ population: _, ...location }) => location);
  }, [data]);
  const addressOptions = data?.marketplace.addressSearch ?? [];

  useEffect(() => {
    if (suggestions.error) {
      reportLocationSuggestionsError(suggestions.error);
    }
  }, [suggestions.error]);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollTop = 0;
    }
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [suggestions.data, suggestions.error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsLocationFocused(false);
        setIsDropdownFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSearch = (search: string) => {
    setQuery(search);
  };

  const handleLocationFocus = () => {
    setSelectionLabel(undefined);
    setIsLocationFocused(true);
    setQuery('');
    setSelectedLocation(undefined);
    setSelectedAddress(undefined);
  };
  const handleDropdownFocus = () => setIsDropdownFocused(true);
  const handleDropdownBlur = () => {
    setIsLocationFocused(false);
    setIsDropdownFocused(false);
  };

  const handleLocationSelect = (location: MapLocation) => {
    setSelectionLabel(location.label);
    rememberRecentSearch(recentEntryFromLocation(location));
    setSelectedLocation(location);
    setSelectedAddress(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const handleAddressSelect = (address: MarketplacePropertySuggestionFragment) => {
    setSelectionLabel(undefined);
    rememberRecentSearch(recentEntryFromAddress(address));
    setSelectedAddress(address);
    setSelectedLocation(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const handleRecentSelect = (entry: RecentLocationSearchEntry) => {
    if (entry.kind === 'zone') {
      handleLocationSelect(mapLocationFromRecentEntry(entry));
      return;
    }
    handleAddressSelect(addressFromRecentEntry(entry));
  };

  const handleRecentRemove = (entry: RecentLocationSearchEntry) => {
    removeRecentSearch(entry);
  };

  const hasSuggestionResults = locationOptions.length > 0 || addressOptions.length > 0;

  const showNoResultsMessage =
    query.length > QUERY_STRING_MIN_LENGTH &&
    !isFetchingSuggestions &&
    !fetchError &&
    !hasSuggestionResults;

  const showRecentSection =
    query.length === 0 && recentSearches.length > 0 && (isLocationFocused || isDropdownFocused);

  const shouldShowDropdown =
    showRecentSection ||
    (Boolean(query) &&
      (isLocationFocused || isDropdownFocused) &&
      (hasSuggestionResults || showNoResultsMessage || isFetchingSuggestions || fetchError));

  // The floor keeps the transient single-line states (searching, error, empty)
  // from rendering as a sliver; rows already exceed it and would otherwise sit
  // above dead space.
  const showsRows = showRecentSection || (Boolean(query) && hasSuggestionResults);

  return (
    <Col className="w-full" ref={containerRef}>
      <Text as="label" className="text-label-md pb-3 text-od-content-primary max-md:hidden">
        Location
      </Text>
      <Input
        aria-label="Location search"
        id="locations-search"
        name="locations"
        size={size}
        placeholder="Search location"
        onChange={(e) => handleLocationSearch(e.target.value)}
        onFocus={handleLocationFocus}
        defaultValue={selectedAddress?.fullAddress || selectedLocation?.label || selectionLabel}
        className={cn('w-full max-md:pr-10', isFocused && 'max-md:pl-10')}
        key={selectedAddress?.fullAddress || selectedLocation?.label || selectionLabel}
      />
      <Box id="locations" className="relative w-full">
        <Col
          ref={resultsRef}
          aria-label="Location suggestions"
          className={cn(
            'absolute left-0 top-2 z-[10000] max-h-100 w-full overflow-x-hidden overflow-y-auto rounded-2xl border border-od-border-accent-brand bg-od-bg-primary',
            showsRows ? 'min-h-0' : 'min-h-30',
            shouldShowDropdown ? 'flex' : 'hidden',
          )}
          onFocus={handleDropdownFocus}
          onBlur={handleDropdownBlur}
        >
          {showRecentSection ? (
            <Col className="w-full bg-od-bg-primary">
              <Row className="w-full items-center justify-between pl-5 pr-3 pt-2">
                <Text as="p" className="text-label-sm">
                  Recent
                </Text>
                <Box
                  aria-label="Clear all recent searches"
                  analyticsName="cosmos-map-clear-recent-searches"
                  onMouseDown={(event: ReactMouseEvent) => {
                    event.preventDefault();
                  }}
                  onClick={clearRecentSearches}
                  className="rounded-lg px-2 py-1 hover:bg-od-bg-secondary"
                >
                  <Text as="span" className="text-label-sm text-od-content-secondary">
                    Clear all
                  </Text>
                </Box>
              </Row>
              {recentSearches.map((entry) => {
                const displayName = recentEntryDisplayName(entry);
                return (
                  <Row
                    key={recentEntryIdentity(entry)}
                    className="w-full items-center gap-1 border-b border-od-border-secondary pr-2 hover:bg-od-bg-secondary"
                  >
                    <Box
                      aria-label={`Select recent ${displayName}`}
                      analyticsName={
                        entry.kind === 'zone'
                          ? 'cosmos-map-select-recent-location'
                          : 'cosmos-map-select-recent-address'
                      }
                      onClick={() => handleRecentSelect(entry)}
                      className="relative min-w-0 flex-1 flex-row px-5 py-5 active:bg-od-bg-secondary"
                    >
                      {entry.kind === 'zone' ? (
                        <Text as="span" className="text-body-sm">
                          {entry.label} {zoneTypeLabel[entry.zoneType]}
                        </Text>
                      ) : (
                        <Col className="items-start">
                          <Text as="p" className="text-body-sm w-full">
                            {displayName}
                          </Text>
                          {entry.listPriceCents ? (
                            <Text as="p" className="text-label-sm self-start">
                              {formatCurrency(entry.listPriceCents)}
                            </Text>
                          ) : null}
                        </Col>
                      )}
                    </Box>
                    <Box
                      aria-label={`Remove ${displayName} from recent searches`}
                      analyticsName="cosmos-map-remove-recent-search"
                      onMouseDown={(event: ReactMouseEvent) => {
                        event.preventDefault();
                      }}
                      onClick={(event: ReactMouseEvent) => {
                        event.stopPropagation();
                        handleRecentRemove(entry);
                      }}
                      className="shrink-0 items-center justify-center rounded-lg p-2 hover:bg-od-bg-hover-tertiary"
                    >
                      <IconX size={18} aria-hidden />
                    </Box>
                  </Row>
                );
              })}
            </Col>
          ) : null}
          {isFetchingSuggestions ? (
            <Box className="w-full px-5 py-5">
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Searching…
              </Text>
            </Box>
          ) : null}
          {fetchError ? (
            <Box className="w-full px-5 py-5" role="alert">
              <Text as="p" className="text-body-sm text-od-content-primary">
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : null}
          {showNoResultsMessage ? (
            <Col className="w-full gap-2 px-5 py-5" role="status" aria-live="polite">
              <Text as="p" className="text-body-sm text-od-content-primary">
                No matches yet
              </Text>
              <Text as="p" className="text-label-sm text-od-content-secondary">
                Try a street number and name, ZIP code, or city. You can also pan the map to search
                this area.
              </Text>
            </Col>
          ) : null}
          {query && locationOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-2">
                Locations
              </Text>
              {locationOptions.map((item, index) => (
                <Col
                  key={item.value}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.label}`}
                    analyticsName="comos-map-select-location"
                    onClick={() => handleLocationSelect(item)}
                    disabled={isFetchingSuggestions || fetchError}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Text as="span" className="text-body-sm">
                      {item.label} {zoneTypeLabel[item.zoneType]}
                    </Text>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
          {query && addressOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-6">
                Listings
              </Text>
              {addressOptions.map((item, index) => (
                <Col
                  key={item.addressId}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.fullAddress}`}
                    analyticsName="cosmos-map-select-address"
                    onClick={() => handleAddressSelect(item)}
                    disabled={isFetchingSuggestions || fetchError}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Col className="items-start">
                      <Text as="p" className="text-body-sm w-full">
                        {item.fullAddress}
                      </Text>
                      {item.listPriceCents && (
                        <Text as="p" className="text-label-sm self-start">
                          {formatCurrency(item.listPriceCents)}
                        </Text>
                      )}
                    </Col>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
        </Col>
      </Box>
    </Col>
  );
};
